import type { MarkPrepUsedResponse, PrepListItem, PrepUsageFlag, UndoPrepUsedResponse } from '../api/prep'
import usePrepDashboardStore, { type PrepDashboardUsedSnapshot } from './prepDashboardStore'
import usePrepListStore from './prepListStore'
import { syncAuthoritativeMyPrepOrder } from './prepMembershipSync'
import usePrepUsageStore from './prepUsageStore'

export interface PrepUsedMutationSnapshot {
  sequence: number
  owner: boolean
  item: PrepListItem
  wasInMyPrep: boolean
  previousMyPrepPosition: number
  previousUsage: PrepUsageFlag | null
  optimisticUsage: PrepUsageFlag
  confirmedUsage: PrepUsageFlag | null
  dashboard: PrepDashboardUsedSnapshot
}

const mutationSequenceByItem = new Map<number, number>()
const pendingMutationByItem = new Map<number, PrepUsedMutationSnapshot>()

const nextMutationSequence = (itemId: number): number => {
  const next = (mutationSequenceByItem.get(itemId) ?? 0) + 1
  mutationSequenceByItem.set(itemId, next)
  return next
}

const isCurrentMutation = (snapshot: PrepUsedMutationSnapshot): boolean =>
  mutationSequenceByItem.get(snapshot.item.id) === snapshot.sequence

type BeginPrepUsedInput = {
  item: PrepListItem
  userId: number
  userName: string
  now?: string
}

const insertAt = (ids: readonly number[], itemId: number, position: number): number[] => {
  const withoutItem = ids.filter((id) => id !== itemId)
  const index = Math.max(0, Math.min(withoutItem.length, position))
  return [...withoutItem.slice(0, index), itemId, ...withoutItem.slice(index)]
}

const makeOptimisticUsage = ({
  item,
  userId,
  userName,
  now = new Date().toISOString(),
}: BeginPrepUsedInput): PrepUsageFlag => {
  const previous = usePrepUsageStore.getState().usageByPrepId.get(item.id) ?? null
  const previousUsages = Array.isArray(previous?.usages) ? previous.usages : []
  const usages = [
    ...previousUsages.filter((entry) => entry.user_id !== userId),
    { user_id: userId, used_at: now, used_by_display_name: userName },
  ]

  return {
    prep_item_id: item.id,
    used: true,
    used_at: now,
    used_by_user_id: userId,
    used_by_display_name: userName,
    usages,
    latest_used_at: now,
    latest_user_id: userId,
  }
}

export const beginPrepUsedOptimistic = (input: BeginPrepUsedInput): PrepUsedMutationSnapshot => {
  const pending = pendingMutationByItem.get(input.item.id)
  if (pending) {
    return { ...pending, owner: false }
  }

  const listStore = usePrepListStore.getState()
  const previousOrder = [...listStore.myAllOrderedIds]
  const previousMyPrepPosition = previousOrder.indexOf(input.item.id)
  const wasInMyPrep = Boolean(input.item.is_in_my_prep || listStore.myIds.has(input.item.id))
  const previousUsage = usePrepUsageStore.getState().usageByPrepId.get(input.item.id) ?? null
  const optimisticUsage = makeOptimisticUsage(input)
  const dashboard = usePrepDashboardStore.getState().optimisticallyMarkUsed(input.item)

  if (wasInMyPrep) {
    listStore.updateMyItem(input.item, false)
  }
  usePrepUsageStore.getState().setUsageFlag(input.item.id, optimisticUsage)

  const snapshot: PrepUsedMutationSnapshot = {
    sequence: nextMutationSequence(input.item.id),
    owner: true,
    item: input.item,
    wasInMyPrep,
    previousMyPrepPosition,
    previousUsage,
    optimisticUsage,
    confirmedUsage: null,
    dashboard,
  }
  pendingMutationByItem.set(input.item.id, snapshot)
  return snapshot
}

export const commitPrepUsed = (
  snapshot: PrepUsedMutationSnapshot,
  response: MarkPrepUsedResponse,
): boolean => {
  if (!isCurrentMutation(snapshot)) {
    return false
  }
  if (response.removed_from_my_prep === true) {
    snapshot.wasInMyPrep = true
  }
  if (
    typeof response.previous_my_prep_position === 'number' &&
    Number.isFinite(response.previous_my_prep_position)
  ) {
    snapshot.previousMyPrepPosition = Math.max(0, Math.floor(response.previous_my_prep_position))
  }
  snapshot.confirmedUsage = response.flag
  usePrepUsageStore.getState().setUsageFlag(snapshot.item.id, response.flag, response.date)
  syncAuthoritativeMyPrepOrder(response, usePrepListStore.getState().myAllOrderedIds)
  pendingMutationByItem.delete(snapshot.item.id)
  return true
}

const restorePrepUsedSnapshot = (snapshot: PrepUsedMutationSnapshot): void => {
  const listState = usePrepListStore.getState()
  if (snapshot.wasInMyPrep) {
    const restoredOrder = insertAt(
      listState.myAllOrderedIds,
      snapshot.item.id,
      snapshot.previousMyPrepPosition,
    )
    listState.replaceMyPrepOrder(restoredOrder, restoredOrder.length)
  }
  usePrepDashboardStore.getState().rollbackMarkUsed(snapshot.dashboard)
  usePrepUsageStore.getState().setUsageFlag(snapshot.item.id, snapshot.previousUsage)
}

export const rollbackPrepUsed = (snapshot: PrepUsedMutationSnapshot): boolean => {
  if (!isCurrentMutation(snapshot)) {
    return false
  }
  restorePrepUsedSnapshot(snapshot)
  pendingMutationByItem.delete(snapshot.item.id)
  return true
}

export const beginPrepUsedUndoOptimistic = (snapshot: PrepUsedMutationSnapshot): void => {
  snapshot.sequence = nextMutationSequence(snapshot.item.id)
  snapshot.owner = true
  pendingMutationByItem.set(snapshot.item.id, snapshot)
  restorePrepUsedSnapshot(snapshot)
}

export const commitPrepUsedUndo = (
  snapshot: PrepUsedMutationSnapshot,
  response: UndoPrepUsedResponse,
): boolean => {
  if (!isCurrentMutation(snapshot)) {
    return false
  }
  usePrepUsageStore.getState().setUsageFlag(
    snapshot.item.id,
    response.flag?.used ? response.flag : snapshot.previousUsage,
    response.date,
  )
  syncAuthoritativeMyPrepOrder(response, usePrepListStore.getState().myAllOrderedIds)
  pendingMutationByItem.delete(snapshot.item.id)
  return true
}

export const rollbackPrepUsedUndo = (snapshot: PrepUsedMutationSnapshot): boolean => {
  if (!isCurrentMutation(snapshot)) {
    return false
  }
  const listStore = usePrepListStore.getState()
  if (snapshot.wasInMyPrep) {
    listStore.updateMyItem(snapshot.item, false)
  }
  snapshot.dashboard = usePrepDashboardStore.getState().optimisticallyMarkUsed(snapshot.item)
  usePrepUsageStore.getState().setUsageFlag(
    snapshot.item.id,
    snapshot.confirmedUsage ?? snapshot.optimisticUsage,
  )
  pendingMutationByItem.delete(snapshot.item.id)
  return true
}
